import React, { useState, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import About from './component/About';
import BlogPanel from './component/BlogPanel';
import Services from './component/Services';
import Terms from './component/Terms';
import '../style/Home.css';

const PANEL_SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
]);

// Scroll the open panel's top (← Home / title) to just under the sticky header.
// Hero collapse is a height transition, so its current layout height is still
// in the flow and must be subtracted to land on the post-collapse position.
const scrollPanelTopIntoView = (scroller, target, header, hero, behavior) => {
  const headerHeight = header.getBoundingClientRect().height;
  const heroHeight = hero ? hero.getBoundingClientRect().height : 0;
  const top = Math.max(
    0,
    target.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop
      - heroHeight
      - headerHeight
  );
  scroller.scrollTo({ top, behavior });
};

const Home = () => {
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [activeView, setActiveView] = useState('home');
  const [navOpen, setNavOpen] = useState(false);
  const mainRef = useRef(null);
  const headerRef = useRef(null);
  const panelTopRef = useRef(null);
  const heroRef = useRef(null);
  const heroInnerRef = useRef(null);
  const aboutRef = useRef(null);
  const aboutInnerRef = useRef(null);
  const blogRef = useRef(null);
  const blogInnerRef = useRef(null);
  const heroHasAnimated = useRef(false);
  const aboutHasAnimated = useRef(false);
  const blogHasAnimated = useRef(false);

  const openOverlay = (overlayName) => {
    setActiveOverlay(overlayName);
  };

  const closeOverlay = () => {
    setActiveOverlay(null);
  };

  const goHome = () => {
    setActiveView('home');
    setNavOpen(false);
  };

  const goAbout = () => {
    setActiveView((v) => (v === 'about' ? 'home' : 'about'));
    setNavOpen(false);
  };

  const goBlog = () => {
    setActiveView((v) => (v === 'blog' ? 'home' : 'blog'));
    setNavOpen(false);
  };

  const isPanelView = activeView === 'about' || activeView === 'blog';

  const toggleNavMenu = () => setNavOpen((o) => !o);
  const closeNavMenu = () => setNavOpen(false);
  const goContact = () => {
    setNavOpen(false);
    window.location.href = 'mailto:projects@aberrest.com';
  };

  // Measured pixel height — smoother than grid-template-rows
  useLayoutEffect(() => {
    const el = heroRef.current;
    const inner = heroInnerRef.current;
    if (!el || !inner) return undefined;

    if (!heroHasAnimated.current) {
      heroHasAnimated.current = true;
      el.style.height = 'auto';
      return undefined;
    }

    let cancelled = false;
    const onEnd = (e) => {
      if (e.target !== el || e.propertyName !== 'height') return;
      if (activeView === 'home' && !cancelled) {
        el.style.height = 'auto';
      }
      el.removeEventListener('transitionend', onEnd);
    };

    if (activeView !== 'home') {
      const from = el.getBoundingClientRect().height || inner.scrollHeight;
      el.style.height = `${from}px`;
      // force reflow so the browser registers the start height
      void el.offsetHeight;
      el.style.height = '0px';
    } else {
      el.style.height = '0px';
      void el.offsetHeight;
      el.style.height = `${inner.scrollHeight}px`;
      el.addEventListener('transitionend', onEnd);
    }

    return () => {
      cancelled = true;
      el.removeEventListener('transitionend', onEnd);
    };
  }, [activeView]);

  // About panel: expand downward on about view; collapse otherwise (mirrors hero)
  useLayoutEffect(() => {
    const el = aboutRef.current;
    const inner = aboutInnerRef.current;
    if (!el || !inner) return undefined;

    if (!aboutHasAnimated.current) {
      aboutHasAnimated.current = true;
      el.style.height = activeView === 'about' ? 'auto' : '0px';
      return undefined;
    }

    let cancelled = false;
    const onEnd = (e) => {
      if (e.target !== el || e.propertyName !== 'height') return;
      if (activeView === 'about' && !cancelled) {
        el.style.height = 'auto';
      }
      el.removeEventListener('transitionend', onEnd);
    };

    if (activeView === 'about') {
      el.style.height = '0px';
      void el.offsetHeight;
      el.style.height = `${inner.scrollHeight}px`;
      el.addEventListener('transitionend', onEnd);
    } else {
      const from = el.getBoundingClientRect().height || inner.scrollHeight;
      el.style.height = `${from}px`;
      void el.offsetHeight;
      el.style.height = '0px';
    }

    return () => {
      cancelled = true;
      el.removeEventListener('transitionend', onEnd);
    };
  }, [activeView]);

  // Blog panel: expand on blog view; collapse otherwise (same measured-height pattern)
  useLayoutEffect(() => {
    const el = blogRef.current;
    const inner = blogInnerRef.current;
    if (!el || !inner) return undefined;

    if (!blogHasAnimated.current) {
      blogHasAnimated.current = true;
      el.style.height = activeView === 'blog' ? 'auto' : '0px';
      return undefined;
    }

    let cancelled = false;
    const onEnd = (e) => {
      if (e.target !== el || e.propertyName !== 'height') return;
      if (activeView === 'blog' && !cancelled) {
        el.style.height = 'auto';
      }
      el.removeEventListener('transitionend', onEnd);
    };

    if (activeView === 'blog') {
      el.style.height = '0px';
      void el.offsetHeight;
      el.style.height = `${inner.scrollHeight}px`;
      el.addEventListener('transitionend', onEnd);
    } else {
      const from = el.getBoundingClientRect().height || inner.scrollHeight;
      el.style.height = `${from}px`;
      void el.offsetHeight;
      el.style.height = '0px';
    }

    return () => {
      cancelled = true;
      el.removeEventListener('transitionend', onEnd);
    };
  }, [activeView]);

  // Opening About or Blog from anywhere on the page should show the panel
  // header under the sticky bar. Desktop polygon nav and the mobile drawer
  // both go through activeView, so one alignment covers both.
  useLayoutEffect(() => {
    if (activeView !== 'about' && activeView !== 'blog') return undefined;

    const scroller = mainRef.current;
    const target = panelTopRef.current;
    const header = headerRef.current;
    if (!scroller || !target || !header) return undefined;

    let cancelled = false;
    let userAdjusted = false;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';

    const align = (nextBehavior) => {
      if (cancelled || userAdjusted) return;
      scrollPanelTopIntoView(scroller, target, header, heroRef.current, nextBehavior);
    };

    const markUserAdjusted = () => {
      userAdjusted = true;
    };

    const onKeyDown = (event) => {
      if (PANEL_SCROLL_KEYS.has(event.key)) markUserAdjusted();
    };

    const onTransitionEnd = (event) => {
      if (event.propertyName !== 'height') return;
      // Height tweens above the panel finish here; snap to the settled layout.
      align('auto');
    };

    // Keep the pre-open anchor (often mid-Services) from winning while the
    // hero collapses and the panel grows above it.
    const previousAnchor = scroller.style.overflowAnchor;
    scroller.style.overflowAnchor = 'none';
    align(behavior);

    const heroEl = heroRef.current;
    const panelEl = activeView === 'about' ? aboutRef.current : blogRef.current;
    heroEl?.addEventListener('transitionend', onTransitionEnd);
    panelEl?.addEventListener('transitionend', onTransitionEnd);
    window.addEventListener('wheel', markUserAdjusted, { passive: true });
    window.addEventListener('touchmove', markUserAdjusted, { passive: true });
    window.addEventListener('keydown', onKeyDown);

    const restoreTimer = window.setTimeout(() => {
      if (!cancelled) {
        align('auto');
        scroller.style.overflowAnchor = previousAnchor;
      }
    }, 750);

    return () => {
      cancelled = true;
      window.clearTimeout(restoreTimer);
      heroEl?.removeEventListener('transitionend', onTransitionEnd);
      panelEl?.removeEventListener('transitionend', onTransitionEnd);
      window.removeEventListener('wheel', markUserAdjusted);
      window.removeEventListener('touchmove', markUserAdjusted);
      window.removeEventListener('keydown', onKeyDown);
      scroller.style.overflowAnchor = previousAnchor;
    };
  }, [activeView]);

  return (
    <div className={`main${isPanelView ? ` view-${activeView}` : ''}`} ref={mainRef}>
      <header className="header" ref={headerRef}>
          <div className="brand-block" onClick={goHome} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); } }}>
              <h1 className="title">Aberrest</h1>
              <h2 className="subtitle">Applied AI for Alts</h2>
          </div>
          <div className="logo-animated" onClick={goHome} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); } }}></div>
      </header>

      <div className="page-body">
        <div
          ref={heroRef}
          className={`hero-block${activeView !== 'home' ? ' is-collapsed' : ''}`}
        >
          <div className="hero-block-inner" ref={heroInnerRef}>
          <h1 className="banner">
            Knowledge Engines for <br className="banner-break" />Alternative Investments
          </h1>
          <h3 className="subbanner">We build the systems that ingest, store, and serve your firm&apos;s knowledge to LLMs.</h3>
          <a href="mailto:projects@aberrest.com" className="hero-cta">
            Contact Us
          </a>
          </div>
        </div>
        <div className="feature-row">
        <button
          type="button"
          className={`nav-menu-toggle${navOpen ? ' is-open' : ''}`}
          aria-label={navOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={navOpen}
          aria-controls="primary-nav"
          onClick={toggleNavMenu}
        >
          <span className="nav-menu-bar" aria-hidden="true" />
          <span className="nav-menu-bar" aria-hidden="true" />
          <span className="nav-menu-bar" aria-hidden="true" />
        </button>
        {navOpen ? (
          <div className="nav-menu-backdrop" onClick={closeNavMenu} aria-hidden="true" />
        ) : null}
        {/* Keeps FeatureRow width while NavStack is fixed on screen */}
        <div className="nav-stack-slot" aria-hidden="true" />
        <nav id="primary-nav" className={`nav-stack${navOpen ? ' is-open' : ''}`} aria-label="Primary">
          <div
            className={`nav-slab nav-slab--about${activeView === 'about' ? ' is-active' : ''}`}
            onClick={goAbout}
          >
            <span className="nav-label">About</span>
          </div>

          <div
            className={`nav-slab nav-slab--blog${activeView === 'blog' ? ' is-active' : ''}`}
            onClick={goBlog}
          >
            <span className="nav-label">Blog</span>
          </div>
          
          <div className="nav-slab nav-slab--contact" onClick={goContact}>
            <span className="nav-label">Contact</span>
          </div>
  
        </nav>
        <div className="content-panel">
          <div
              ref={panelTopRef}
              className={`back-button${isPanelView ? '' : ' is-hidden'}`}
              onClick={goHome}
              role="button"
              tabIndex={isPanelView ? 0 : -1}
              aria-hidden={!isPanelView}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  goHome();
                }
              }}
            >
              ← Home
            </div>
          <div
            ref={aboutRef}
            className={`about-expand${activeView === 'about' ? ' is-expanded' : ''}`}
            aria-hidden={activeView !== 'about'}
          >
            <div className="about-expand-inner" ref={aboutInnerRef}>
              <About onGoHome={goHome} />
            </div>
          </div>
          <div
            ref={blogRef}
            className={`blog-expand${activeView === 'blog' ? ' is-expanded' : ''}`}
            aria-hidden={activeView !== 'blog'}
            {...(activeView !== 'blog' ? { inert: '' } : {})}
          >
            <div className="blog-expand-inner" ref={blogInnerRef}>
              <BlogPanel onGoHome={goHome} />
            </div>
          </div>
          <Services />
          </div>
        </div>
      </div>

      {activeOverlay && (
        <div className="overlay active" onClick={closeOverlay}>
          <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
            <span className="overlay-close" onClick={closeOverlay}>&times;</span>
            {activeOverlay === 'terms' && <Terms />}
          </div>
        </div>
      )}

      <footer className="footer">
        <a href="#" onClick={(e) => { e.preventDefault(); openOverlay('terms'); }}>Terms of Use</a>
        <Link to="/privacy">Privacy Policy</Link>
        <span> Aberrest © 2021-Present. All rights reserved.</span>
      </footer>
    </div>
  );
};

export default Home;
